import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { ServiceProductModel } from '@models/serviceProduct'
import type { AppDispatch } from '@redux/store'
import type {
  Action, ActionResult, EntityOptions, Errors, FetchRequestPayload, FetchSuccessPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/serviceProduct/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/serviceProduct/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/serviceProduct/FETCH_FAILURE'

export type ServiceProductModuleState = {
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
}

// Initial State
const initialState: ServiceProductModuleState = {
  errors: [],
  loaded: false,
  loadedIds: [],
  loadedForKeys: [],
  loading: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess(payload: FetchSuccessPayload = {}) {
  return {
    type: FETCH_SUCCESS,
    loadedIds: payload.loadedIds,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

// Action Creators
export function loadServiceProducts(options: EntityOptions = {}) {
  const { entityKey, provider_ids } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState): Promise<ActionResult<ServiceProductModel>> => {
    const loadedForKeys = [...getState().serviceProducts.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const updatedOptions = { ...options }
    if (provider_ids){
      updatedOptions.provider_ids = JSON.stringify(provider_ids)
    }

    const promise = api('/service_products.json', updatedOptions)
      .then(({ data }: { data: ServiceProductModel[] }) => {
        const normalizedJson = normalize(data, Schemas.SERVICE_PRODUCT_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))

        const loadedIds = data.map(p => p.id)
        // @ts-expect-error TS2322
        dispatch(fetchSuccess({ loadedIds }))

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    // @ts-expect-error TS2322
    return promise
  }
}

export function loadServiceProductIds(serviceProductIds: number[], options: EntityOptions = {}) {
  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedIds = [...state.serviceProducts.loadedIds]
    const mergedIds = [...loadedIds, ...serviceProductIds]
    const uniqueIds = Array.from(new Set(mergedIds))

    const missingIds = serviceProductIds.filter(id => !loadedIds.includes(id))
    if (!missingIds.length){
      return new Promise((resolve) => {
        resolve({ success: true as const, data: [] })
      })
    }

    // Set Loading
    dispatch(fetchRequest({ loadedIds: uniqueIds }))

    const promise = api('/service_products.json', { ...options, service_product_ids: JSON.stringify(missingIds) })
      .then(({ data }: { data: ServiceProductModel[] }) => {
        const normalizedJson = normalize(data, Schemas.SERVICE_PRODUCT_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadServiceProduct(serviceProductId: number, options: EntityOptions = {}) {
  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedIds = [...state.serviceProducts.loadedIds]

    if (serviceProductId && !loadedIds.includes(serviceProductId)){
      loadedIds.push(serviceProductId)
    }

    // Set Loading
    dispatch(fetchRequest({ loadedIds }))

    const promise = api(`/service_products/${serviceProductId}.json`, options)
      .then(({ data }: { data: ServiceProductModel }) => {
        const normalizedJson = normalize(data, Schemas.SERVICE_PRODUCT)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: ServiceProductModuleState = initialState,
  action: Action = { type: '' },
): ServiceProductModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds ? Array.from(new Set([...state.loadedIds, ...action.loadedIds])) : state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loaded: true,
        loadedIds: action.loadedIds ? Array.from(new Set([...state.loadedIds, ...action.loadedIds])) : state.loadedIds,
        loading: false,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
